/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/prop-types */
import React, {useEffect, useRef} from 'react';
import {Platform, ScrollView, View} from 'react-native';
import {useSelector} from 'react-redux';
import {createStackNavigator} from '@react-navigation/stack';
import {noop} from 'lodash';

import ModalHeader from '/component/layout/ModalHeader/ModalHeader';
import NavigationHeader from '/component/layout/NavigationHeader';
import InfoPopover from '/component/partial/InfoPopover';
import {CreateCandidateProfile} from '/component/screen/CandidateProfile/CreateCandidateProfile';
import Contact from '/component/screen/ClientProfile/Contact/Contact.connect';
import CreditsAndRewards from '/component/screen/ClientProfile/CreditsAndRewards/CreditsAndRewards.connect';
import Details from '/component/screen/ClientProfile/Details/Details.connect';
import NextPremierOrder from '/component/screen/ClientProfile/NextPremierOrder/NextPremierOrder.connect';
import OrderDetail from '/component/screen/ClientProfile/OrderDetail/OrderDetail.connect';
import OrderHistory from '/component/screen/ClientProfile/OrderHistory/OrderHistory.connect';
import Header from '/component/screen/CoachProfile/Header/Header.connect';
import MyTeamsContent from '/component/screen/report/MyTeamsReport/MyTeamsContent.connect';
import {Search} from '/component/screen/Search';
import {OrderDetails} from '/constant/TestIDs';
import {
  CLIENT_PROFILE,
  CREATE_NEW_CANDIDATE,
  ORDER_DETAILS,
  SEARCH,
} from '/enum/records.enum';
import {
  CLIENT_PROFILE as CLIENT_PROFILE_TYPE,
  COACH_PROFILE,
} from '/enum/recordType.enum';
import {EVENT_NAMES, SCREEN_NAMES} from '/enum/tealium.enum';
import {createPopoverProvider} from '/provider/PopoverProvider';
import {orderNumberById} from '/redux/selector/client.selector';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {getReportScreenComponent} from './MainRecordsScreen/MainRecordScreen';
import {FilterScreen} from './FilterScreen';
import RecordScreenRenderer from './RecordScreenRenderer';
import {styles} from './RecordsScreen.style';
import {SortScreen} from './SortScreen';

import {RecordsFilterContextProvider} from '/context/RecordsFilter';
import {RecordsModalContextProvider} from '/context/RecordsModal';
import {RecordsSortContextProvider} from '/context/RecordsSort';
interface RecordsScreenProps {
  contentComponent: React.ReactNode;
  contentProps: any;
  title: React.ReactNode;
  description: React.ReactNode;
  testID: string;
  recordId: number;
  onClose: () => void;
  reportName: string;
  lastPaidRank: string;
}
export class RecordsScreen extends React.PureComponent<RecordsScreenProps> {
  popoverProvider: any;

  constructor(props: RecordsScreenProps) {
    super(props);
    this.popoverProvider = createPopoverProvider();
  }

  getRecordScreenComponent(props: any) {
    return ({route, navigation: {canGoBack, goBack, navigate, push}}: any) => {
      const {reportName} = this.props;
      const {recordId, recordType} = route.params || {};

      const handleCloseRef = useRef(() =>
        canGoBack() ? goBack() : navigate('records-main-screen'),
      );

      const handleShowRecordRef = useRef((params = {}) =>
        push('records-record-screen', params),
      );

      return (
        <RecordScreenRenderer
          onClose={handleCloseRef.current}
          showReportRecordScreen={handleShowRecordRef.current}
          recordId={recordId}
          recordType={recordType}
          reportName={reportName}
        />
      );
    };
  }

  getOrderDetailScreenComponent() {
    const {recordId = 0, lastPaidRank} = this.props;
    return ({
      route: {params: {orderId} = {}} = {},
      navigation: {navigate, push},
    }: any) => {
      const orderNumber = useSelector((state: any) =>
        orderNumberById(state, {orderId}),
      );
      const handleShowRecordRef = useRef((params: any = {}) =>
        push('records-record-screen', {
          ...params,
          modalName: params.recordType,
        }),
      );

      useEffect(() => {
        const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
          eventName: EVENT_NAMES.onPageLoad,
        });
        sendEvent({
          currentScreenName: SCREEN_NAMES.orderDetails,
          previousScreenName: SCREEN_NAMES.reportCard,
          lastPaidRank: lastPaidRank,
        });
      }, []);

      const onShowRecord = handleShowRecordRef.current;

      const handleViewProfileRef = useRef(({clientId, coachId}: any) => {
        if ((clientId || coachId) === recordId) {
          // If the customer of the order is the same as the current record, then there is no need
          // to call `onShowRecord` as we can simply navigate to the main screen.
          navigate('client-profile-main-screen', {modalName: CLIENT_PROFILE});
        } else {
          // If the customer of the order is not the same as the current record, then we have to
          // call `onShowRecord` to navigate to the profile screen of that customer.
          if (clientId) {
            onShowRecord({recordType: CLIENT_PROFILE_TYPE, recordId: clientId});
          } else if (coachId) {
            onShowRecord({recordType: COACH_PROFILE, recordId: coachId});
          }
        }
      });

      return (
        <NavigationHeader
          style={{base: styles.base2}}
          title={Platform.OS === 'web' ? `Order #${orderNumber || ''}` : null}
          leftIconTestIDs={[OrderDetails.screenBackButton]}>
          <View style={styles.content}>
            <OrderDetail
              // @ts-expect-error TS(2322): Type 'any' is not assignable to type 'never'.
              orderId={orderId}
              hideHeadingOnWeb={Platform.OS === 'web'}
              // @ts-expect-error TS(2322): Type '({ clientId, coachId }: any) => void' is not... Remove this comment to see the full error message
              onViewProfile={handleViewProfileRef.current}
            />
          </View>
        </NavigationHeader>
      );
    };
  }

  getProfileScreenComponent() {
    const {Consumer: PopoverConsumer} = this.popoverProvider;
    const {onClose = noop, recordId = 0} = this.props;

    return ({navigation, push}: any) => {
      const handleShowRecordRef = useRef((params: any = {}) =>
        push('records-record-screen', {
          ...params,
          modalName: params.recordType,
        }),
      );

      const onShowRecord = handleShowRecordRef.current;

      const ScreenComponent = ({popoverShow}: any) => {
        const handleViewMoreOrderHistoryRef = useRef(() =>
          navigate('client-profile-order-history-list-screen'),
        );

        const handleOrderPressRef = useRef(({orderId}: any) => {
          push('client-profile-order-detail-screen', {
            orderId,
            modalName: ORDER_DETAILS,
          });
        });
        return (
          // @ts-expect-error TS(2769): No overload matches this call.
          <ModalHeader onClose={onClose} style={{base: styles.base}}>
            <View style={styles.content}>
              <Header clientId={recordId} />
              <ScrollView>
                <View style={styles.sections}>
                  <Details
                    clientId={recordId}
                    onCoachPress={({coachId}: any) =>
                      onShowRecord({
                        recordType: COACH_PROFILE,
                        recordId: coachId,
                      })
                    }
                  />
                  <Contact clientId={recordId} />
                  <NextPremierOrder clientId={recordId} />
                  <CreditsAndRewards clientId={recordId} />
                  <OrderHistory
                    clientId={recordId}
                    onOrderPress={handleOrderPressRef.current}
                    onViewMore={handleViewMoreOrderHistoryRef.current}
                  />
                  {/*<Notes clientId={recordId} />*/}
                </View>
              </ScrollView>
            </View>
          </ModalHeader>
        );
      };

      return (
        <PopoverConsumer>
          {({popoverShow}: any) => (
            <ScreenComponent popoverShow={popoverShow} />
          )}
        </PopoverConsumer>
      );
    };
  }

  getSortScreenComponent() {
    const {Consumer: PopoverConsumer} = this.popoverProvider;

    return () => {
      const SortScreenComponent = ({popoverShow}: any) => {
        const handleShowInfoPopoverRef = useRef((payload: any) =>
          popoverShow(InfoPopover, {
            ...payload,
            useModal: Platform.OS === 'android',
          }),
        );
        return (
          <SortScreen onShowInfoPopover={handleShowInfoPopoverRef.current} />
        );
      };

      return (
        <PopoverConsumer>
          {({popoverShow}: any) => (
            <SortScreenComponent popoverShow={popoverShow} />
          )}
        </PopoverConsumer>
      );
    };
  }

  getCreateCandidateComponent = () => {
    return () => {
      return <CreateCandidateProfile />;
    };
  };

  getMyTeamsComponent = () => {
    // @ts-expect-error TS(2339): Property 'contentProps' does not exist on type '...'
    const {contentProps} = this.props;

    return getReportScreenComponent(
      this.popoverProvider,
      {
        ...this.props,
        contentComponent: MyTeamsContent,
        contentProps: {
          ...contentProps,
          persistenceKey: SCREEN_NAMES.myTeamsReport,
        },
        title: 'My Teams',
        description: 'All Coaches in your organization.',
      },
      undefined, // popoverShow if needed
    );
  };

  searchComponent = () => {
    return () => {
      return <Search />;
    };
  };

  getRecordsStackComponent() {
    const RecordsStackNavigator = createStackNavigator();
    // @ts-expect-error TS(2554): Expected 3 arguments, but got 2.
    const MainScreen = getReportScreenComponent(
      this.popoverProvider,
      this.props,
    );
    const RecordScreen = this.getRecordScreenComponent(this.props);
    const SortScreenComponent = this.getSortScreenComponent();
    const ProfileScreen = this.getProfileScreenComponent();
    const OrderDetailScreen = this.getOrderDetailScreenComponent();
    const CreateCandidate = this.getCreateCandidateComponent();
    const Myteams = this.getMyTeamsComponent();
    const Search = this.searchComponent();

    return () => (
      <RecordsStackNavigator.Navigator
        initialRouteName="records-main-screen"
        screenOptions={{headerShown: false}}>
        <RecordsStackNavigator.Screen
          name="records-main-screen"
          component={MainScreen}
        />
        <RecordsStackNavigator.Screen
          name="records-filter-screen"
          component={FilterScreen}
        />
        <RecordsStackNavigator.Screen
          name="records-record-screen"
          component={RecordScreen}
        />
        <RecordsStackNavigator.Screen
          name="records-sort-screen"
          component={SortScreenComponent}
        />
        <RecordsStackNavigator.Screen
          name="client-profile-order-detail-screen"
          component={OrderDetailScreen}
        />
        <RecordsStackNavigator.Screen
          name="client-profile-main-screen"
          component={ProfileScreen}
        />
        <RecordsStackNavigator.Screen
          name="create-candidate"
          component={CreateCandidate}
          initialParams={{modalName: CREATE_NEW_CANDIDATE}}
        />
        <RecordsStackNavigator.Screen
          name="myteams-data"
          component={Myteams}
          options={{headerShown: false}}
        />
        <RecordsStackNavigator.Screen
          name="search"
          component={Search}
          options={{headerShown: false}}
          initialParams={{modalName: SEARCH}}
        />
      </RecordsStackNavigator.Navigator>
    );
  }

  getPopoverStackComponent() {
    const {Provider: PopoverProvider} = this.popoverProvider;
    const RecordsStack = this.getRecordsStackComponent();

    return () => (
      <PopoverProvider>
        <RecordsStack />
      </PopoverProvider>
    );
  }

  // @ts-expect-error TS(4114): This member must have an 'override' modifier becau... Remove this comment to see the full error message
  render() {
    const PopoverStack = this.getPopoverStackComponent();
    return (
      <RecordsSortContextProvider>
        <RecordsFilterContextProvider>
          <RecordsModalContextProvider>
            <PopoverStack />
          </RecordsModalContextProvider>
        </RecordsFilterContextProvider>
      </RecordsSortContextProvider>
    );
  }
}

export default RecordsScreen;
